import type { TeamMembership, TeamPermissions } from '@rhythmic/db/types'
import type { WorkspaceMemberUser } from '@rhythmic/types'
import type { UseLiveQueryReturn } from '@tanstack/vue-db'
import { eq, useLiveQuery } from '@tanstack/vue-db'
import { useRhythmicLogger } from '#imports'
import { TEAM_MEMBER_TABLE, WORKSPACE_MEMBER_USER_TABLE } from '../collections'

import { useCollection } from '../composables/useCollection'

export interface TeamUser {
  id: string
  team_id: string
  user_id: string
  full_name: string | null
  email: string | null
  avatar_url: string | null
  role: TeamPermissions | null
  admin: boolean | null
  intercom_user: string | null
  stripe_customer_id: string | null
  stripe_subscription_id: string | null
  updated_at: Date
}
export function useTeamUsers(team_id: string, workspace_id: string): UseLiveQueryReturn<TeamUser> | undefined {
  const logger = useRhythmicLogger('useTeamUsers')
  const users = useCollection<WorkspaceMemberUser>(
    WORKSPACE_MEMBER_USER_TABLE,
    { workspaceId: workspace_id },
    'query',
  ).collection
  const teamMembers = useCollection<TeamMembership>(TEAM_MEMBER_TABLE, {
    team_id,
  }).collection

  if (!users || !teamMembers) {
    logger.warn('Collections not available yet')
    return undefined
  }

  const query = useLiveQuery((builder) =>
    builder
      .from({ u: users })
      .innerJoin({ t: teamMembers }, ({ u, t }) => eq(u.id, t.user_id))
      .select(({ u, t }) => ({
        id: t.id,
        team_id: t.team_id,
        user_id: u.id,
        full_name: u.full_name || u.email,
        email: u.email,
        avatar_url: u.avatar_url,
        role: t.permissions as TeamPermissions | null,
        admin: u.admin ?? false,
        intercom_user: u.intercom_user,
        stripe_customer_id: u.stripe_customer_id,
        stripe_subscription_id: u.stripe_subscription_id,
        updated_at: u.updated_at,
      })),
  ) as UseLiveQueryReturn<TeamUser>

  return query
}
